import React, { useEffect, useMemo, useState } from "react";
import { Form, Row, Col, Card } from "react-bootstrap";
import LocationAutocomplete from "../LocationAutocomplete";
import { useTranslation } from "react-i18next";

export default function StepFour({
  selectedOption,
  setSelectedOption,
  email,
  setEmail,
  companyName,
  setCompanyName,
  salutation,
  setSalutation,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  street,
  setStreet,
  addressExtra,
  setAddressExtra,
  postalCity,
  setPostalCity,
  phone,
  setPhone,
  vatId,
  setVatId,
  validationErrors = {},
  pickupForms = [],
  deliveryForms = [],
}) {
  const { t } = useTranslation();
  const [selectedPickupForm, setSelectedPickupForm] = useState(0);
  const [selectedDeliveryForm, setSelectedDeliveryForm] = useState(0);

  const options = useMemo(
    () => [
      t("invoice_option_filling"),
      t("invoice_option_like_pickup"),
      t("invoice_option_like_delivery"),
    ],
    [t],
  );

  const isReadOnly = useMemo(
    () =>
      selectedOption === t("invoice_option_like_pickup") ||
      selectedOption === t("invoice_option_like_delivery"),
    [selectedOption, t],
  );

  const handleLocationChange = (e) => {
    const value = e?.target?.value || e?.value || "";
    setPostalCity(value);
  };

  // تحديث البيانات عند تغيير النموذج المختار
  useEffect(() => {
    if (
      selectedOption === t("invoice_option_like_pickup") &&
      pickupForms.length > 0
    ) {
      const selectedForm = pickupForms[selectedPickupForm];
      setCompanyName(selectedForm.companyName || "");
      setSalutation("");
      setFirstName(selectedForm.firstName || "");
      setLastName(selectedForm.lastName || "");
      setStreet(selectedForm.street || "");
      setAddressExtra(selectedForm.addressExtra || "");
      setPostalCity(selectedForm.postalCity || "");
      setPhone(selectedForm.phone || "");
      // setVatId(selectedForm.pickupReference || "");
    } else if (
      selectedOption === t("invoice_option_like_delivery") &&
      deliveryForms.length > 0
    ) {
      const selectedForm = deliveryForms[selectedDeliveryForm];
      setCompanyName(selectedForm.companyName || "");
      setSalutation("");
      setFirstName(selectedForm.firstName || "");
      setLastName(selectedForm.lastName || "");
      setStreet(selectedForm.street || "");
      setAddressExtra(selectedForm.addressExtra || "");
      setPostalCity(selectedForm.postalCity || "");
      setPhone(selectedForm.phone || "");
      // setVatId(selectedForm.deliveryReference || "");
    }
  }, [
    selectedOption,
    selectedPickupForm,
    selectedDeliveryForm,
    pickupForms,
    deliveryForms,
    setCompanyName,
    setSalutation,
    setFirstName,
    setLastName,
    setStreet,
    setAddressExtra,
    setPostalCity,
    setPhone,
    setVatId, 
    t,
  ]);

  const renderFormSelector = () => {
    if (
      selectedOption === t("invoice_option_like_pickup") &&
      pickupForms.length > 1
    ) {
      return (
        <Card className="p-3 mb-3 shadow-sm">
          <Form.Group>
            <Form.Label className="fw-bold">
              {t("select_pickup_form")}
            </Form.Label>
            <Form.Select
              value={selectedPickupForm}
              onChange={(e) => setSelectedPickupForm(parseInt(e.target.value))}
              className="input-elegant"
            >
              {pickupForms.map((form, index) => (
                <option key={index} value={index}>
                  {t("pickup_form")} {index + 1}: {form.firstName}{" "}
                  {form.lastName} - {form.street}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Card>
      );
    } else if (
      selectedOption === t("invoice_option_like_delivery") &&
      deliveryForms.length > 1
    ) {
      return (
        <Card className="p-3 mb-3 shadow-sm">
          <Form.Group>
            <Form.Label className="fw-bold">
              {t("select_delivery_form")}
            </Form.Label>
            <Form.Select
              value={selectedDeliveryForm}
              onChange={(e) =>
                setSelectedDeliveryForm(parseInt(e.target.value))
              }
              className="input-elegant"
            >
              {deliveryForms.map((form, index) => (
                <option key={index} value={index}>
                  {t("delivery_form")} {index + 1}: {form.firstName}{" "}
                  {form.lastName} - {form.street}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Card>
      );
    }
    return null;
  };

  const renderFormFields = () => (
    <>
      {renderFormSelector()}
      <Card className="p-4 border-0 mt-3" style={{ borderRadius: "16px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
        <Row className="g-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("email_label")} *
              </Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-elegant"
                data-error="invoiceEmail"
                isInvalid={!!validationErrors.invoiceEmail}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.invoiceEmail}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("company_name")}
              </Form.Label>
              <Form.Control
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="input-elegant"
                // readOnly={isReadOnly}
              />
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Label className="fw-bold with-icon">
              {t("salutation_required")} *
            </Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                inline
                label={t("salutation_mr")}
                name="anrede"
                type="radio"
                id="anrede-herr"
                checked={salutation === t("salutation_mr")}
                onChange={() => setSalutation(t("salutation_mr"))}
                data-error="invoiceSalutation"
                isInvalid={!!validationErrors.invoiceSalutation}
              />
              <Form.Check
                inline
                label={t("salutation_ms")}
                name="anrede"
                type="radio"
                id="anrede-frau"
                checked={salutation === t("salutation_ms")}
                onChange={() => setSalutation(t("salutation_ms"))}
                data-error="invoiceSalutation"
                isInvalid={!!validationErrors.invoiceSalutation}
              />
            </div>
            {validationErrors.invoiceSalutation && (
              <div className="text-danger small mt-1">
                {validationErrors.invoiceSalutation}
              </div>
            )}
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("first_name")} *
              </Form.Label>
              <Form.Control
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="input-elegant"
                data-error="invoiceFirstName"
                isInvalid={!!validationErrors.invoiceFirstName}
                // readOnly={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.invoiceFirstName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("last_name")} *
              </Form.Label>
              <Form.Control
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="input-elegant"
                data-error="invoiceLastName"
                isInvalid={!!validationErrors.invoiceLastName}
                // readOnly={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.invoiceLastName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("street_house")} *
              </Form.Label>
              <Form.Control
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className="input-elegant"
                data-error="invoiceStreet"
                isInvalid={!!validationErrors.invoiceStreet}
                // readOnly={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.invoiceStreet}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("address_extra_label")}
              </Form.Label>
              <Form.Control
                type="text"
                value={addressExtra}
                onChange={(e) => setAddressExtra(e.target.value)}
                className="input-elegant"
                // readOnly={isReadOnly}
              />
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold with-icon">
                {t("postal_city")} *
              </Form.Label>
              <LocationAutocomplete
                type="text"
                name="postalCity"
                value={postalCity}
                onChange={handleLocationChange}
                className="input-elegant"
                data-error="invoicePostalCity"
                isInvalid={!!validationErrors.invoicePostalCity}
                // readOnly={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.invoicePostalCity}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("phone_number")} *
              </Form.Label>
              <Form.Control
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-elegant"
                // readOnly={isReadOnly}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold with-icon">
                {t("vat_id_label")} *
              </Form.Label>
              <Form.Control
                type="text"
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
                className="input-elegant"
                // readOnly={isReadOnly}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card>
    </>
  );

  return (
    <div>
      <div className="d-flex flex-wrap gap-4 mb-3">
        {options.map((opt) => (
          <Form.Check
            key={opt}
            inline
            type="radio"
            label={opt}
            name="zusatzOption"
            value={opt}
            checked={selectedOption === opt}
            onChange={() => setSelectedOption(opt)}
          />
        ))}
      </div>
      {selectedOption && renderFormFields()}
    </div>
  );
}
